import {useId, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {User} from '@ui/types/user';
import {AccountSettingsPanel} from '../account-settings-panel';
import {AccountSettingsId} from '../account-settings-sidenav';
import {Button} from '@ui/buttons/button';
import {Form} from '@ui/forms/form';
import {TextField} from '@ui/forms/input-field/text-field/text-field';
import {Trans} from '@ui/i18n/trans';
import {useTrans} from '@ui/i18n/use-trans';
import {message} from '@ui/i18n/message';
import {toast} from '@ui/toast/toast';
import {useIsDarkMode} from '@ui/themes/use-is-dark-mode';
import ReactPhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import {useSendPhoneUpdateOtp} from './use-send-phone-update-otp';
import {
  VerifyPhoneUpdateEmailOtpPayload,
  useVerifyPhoneUpdateEmailOtp,
} from './use-verify-phone-update-email-otp';
import {
  SendPhoneUpdateMobileOtpPayload,
  useSendPhoneUpdateMobileOtp,
} from './use-send-phone-update-mobile-otp';
import {
  VerifyPhoneUpdateMobileOtpPayload,
  useVerifyPhoneUpdateMobileOtp,
} from './use-verify-phone-update-mobile-otp';

interface Props {
  user: User;
}

type Step =
  | 'request-code'
  | 'enter-email-code'
  | 'enter-mobile-number'
  | 'enter-mobile-code';

export function PhoneNumberPanel({user}: Props) {
  const {trans} = useTrans();
  const isDarkMode = useIsDarkMode();
  const formId = useId();
  const [step, setStep] = useState<Step>('request-code');
  // the emailed code stays valid across steps (only consumed once the
  // mobile number is actually saved), so it's carried along and re-checked
  // server-side on every subsequent request
  const [emailCode, setEmailCode] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');

  const sendOtp = useSendPhoneUpdateOtp();

  const emailCodeForm = useForm<VerifyPhoneUpdateEmailOtpPayload>({
    defaultValues: {code: ''},
  });
  const verifyEmailOtp = useVerifyPhoneUpdateEmailOtp(emailCodeForm);

  const mobileNumberForm = useForm<SendPhoneUpdateMobileOtpPayload>({
    defaultValues: {code: '', mobile_number: user.mobile_number || ''},
  });
  const sendMobileOtp = useSendPhoneUpdateMobileOtp(mobileNumberForm);

  const mobileCodeForm = useForm<VerifyPhoneUpdateMobileOtpPayload>({
    defaultValues: {code: '', mobile_number: '', mobile_code: ''},
  });
  const verifyMobileOtp = useVerifyPhoneUpdateMobileOtp(mobileCodeForm);

  const requestCode = () => {
    sendOtp.mutate(undefined, {
      onSuccess: () => {
        toast(message('Verification code sent'));
        setStep('enter-email-code');
      },
    });
  };

  const resetToStart = () => {
    emailCodeForm.reset();
    mobileNumberForm.reset();
    mobileCodeForm.reset();
    setEmailCode('');
    setMobileNumber('');
    setStep('request-code');
  };

  if (step === 'enter-email-code') {
    return (
      <AccountSettingsPanel
        id={AccountSettingsId.PhoneNumber}
        title={<Trans message="Change Mobile Number" />}
        actions={
          <div className="flex items-center gap-14">
            <Button variant="outline" onClick={resetToStart}>
              <Trans message="Back" />
            </Button>
            <Button
              type="submit"
              form={formId}
              variant="flat"
              color="primary"
              disabled={verifyEmailOtp.isPending}
            >
              <Trans message="Continue" />
            </Button>
          </div>
        }
      >
        <p className="mb-24 text-sm text-muted">
          <Trans message="Enter the verification code we sent to your email." />
        </p>
        <Form
          form={emailCodeForm}
          id={formId}
          onSubmit={values => {
            verifyEmailOtp.mutate(values, {
              onSuccess: () => {
                setEmailCode(values.code);
                mobileNumberForm.setValue('code', values.code);
                setStep('enter-mobile-number');
              },
            });
          }}
        >
          <Controller
            name="code"
            control={emailCodeForm.control}
            rules={{required: true}}
            render={({field: {onChange, ...field}, fieldState}) => (
              <TextField
                {...field}
                className="mb-24"
                onChange={e =>
                  onChange(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                invalid={fieldState.invalid}
                errorMessage={fieldState.error?.message}
                label={<Trans message="Code" />}
                placeholder={trans(message('Enter your verification code'))}
                autoFocus
                autoComplete="one-time-code"
                autoCorrect="off"
                autoCapitalize="off"
                maxLength={6}
                inputMode="numeric"
                pattern="[0-9]*"
              />
            )}
          />
          <Button
            variant="link"
            color="primary"
            className="mt-14"
            disabled={sendOtp.isPending}
            onClick={requestCode}
          >
            <Trans message="Resend code" />
          </Button>
        </Form>
      </AccountSettingsPanel>
    );
  }

  if (step === 'enter-mobile-number') {
    return (
      <AccountSettingsPanel
        id={AccountSettingsId.PhoneNumber}
        title={<Trans message="Change Mobile Number" />}
        actions={
          <div className="flex items-center gap-14">
            <Button variant="outline" onClick={resetToStart}>
              <Trans message="Back" />
            </Button>
            <Button
              type="submit"
              form={formId}
              variant="flat"
              color="primary"
              disabled={sendMobileOtp.isPending}
            >
              <Trans message="Send code" />
            </Button>
          </div>
        }
      >
        <p className="mb-24 text-sm text-muted">
          <Trans message="Enter the new mobile number you'd like to use to sign in." />
        </p>
        <Form
          form={mobileNumberForm}
          id={formId}
          onSubmit={values => {
            sendMobileOtp.mutate(values, {
              onSuccess: () => {
                setMobileNumber(values.mobile_number);
                mobileCodeForm.setValue('code', emailCode);
                mobileCodeForm.setValue('mobile_number', values.mobile_number);
                setStep('enter-mobile-code');
              },
            });
          }}
        >
          <label
            className="mobile_label"
            style={{
              backgroundColor: isDarkMode ? 'rgb(29 29 33)' : 'white',
              color: isDarkMode ? 'white' : 'black',
            }}
          >
            <Trans message="New mobile number" />
            <Controller
              name="mobile_number"
              control={mobileNumberForm.control}
              rules={{required: true}}
              render={({field: {onChange, value}, fieldState}) => (
                <div className="mobile_number w-full">
                  <ReactPhoneInput
                    value={value ?? ''}
                    onChange={phone => onChange(phone)}
                    country="in"
                    countryCodeEditable={false}
                    inputStyle={{
                      backgroundColor: isDarkMode ? 'rgb(29 29 33)' : 'white',
                      color: isDarkMode ? 'white' : 'black',
                      width: '100%',
                    }}
                    dropdownStyle={{
                      backgroundColor: isDarkMode ? 'rgb(29 29 33)' : 'white',
                      color: isDarkMode ? 'white' : 'black',
                    }}
                  />
                  {fieldState.error?.message && (
                    <div className="mt-4 text-sm text-danger">
                      {fieldState.error.message}
                    </div>
                  )}
                </div>
              )}
            />
          </label>
        </Form>
      </AccountSettingsPanel>
    );
  }

  if (step === 'enter-mobile-code') {
    return (
      <AccountSettingsPanel
        id={AccountSettingsId.PhoneNumber}
        title={<Trans message="Change Mobile Number" />}
        actions={
          <div className="flex items-center gap-14">
            <Button
              variant="outline"
              onClick={() => setStep('enter-mobile-number')}
            >
              <Trans message="Back" />
            </Button>
            <Button
              type="submit"
              form={formId}
              variant="flat"
              color="primary"
              disabled={verifyMobileOtp.isPending}
            >
              <Trans message="Update phone number" />
            </Button>
          </div>
        }
      >
        <p className="mb-24 text-sm text-muted">
          <Trans
            message="Enter the verification code we sent to :number."
            values={{number: `+${mobileNumber}`}}
          />
        </p>
        <Form
          form={mobileCodeForm}
          id={formId}
          onSubmit={values => {
            verifyMobileOtp.mutate(values);
          }}
        >
          {mobileCodeForm.formState.errors.mobile_number?.message && (
            <div className="mb-24 text-sm text-danger">
              {mobileCodeForm.formState.errors.mobile_number.message}
            </div>
          )}
          <Controller
            name="mobile_code"
            control={mobileCodeForm.control}
            rules={{required: true}}
            render={({field: {onChange, ...field}, fieldState}) => (
              <TextField
                {...field}
                className="mb-24"
                onChange={e =>
                  onChange(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                invalid={fieldState.invalid}
                errorMessage={fieldState.error?.message}
                label={<Trans message="Code" />}
                placeholder={trans(message('Enter your verification code'))}
                autoFocus
                autoComplete="one-time-code"
                autoCorrect="off"
                autoCapitalize="off"
                maxLength={6}
                inputMode="numeric"
                pattern="[0-9]*"
              />
            )}
          />
          <Button
            variant="link"
            color="primary"
            className="mt-14"
            disabled={sendMobileOtp.isPending}
            onClick={() => {
              sendMobileOtp.mutate(
                {code: emailCode, mobile_number: mobileNumber},
                {
                  onSuccess: () => {
                    toast(message('Verification code sent'));
                  },
                },
              );
            }}
          >
            <Trans message="Resend code" />
          </Button>
        </Form>
      </AccountSettingsPanel>
    );
  }

  return (
    <AccountSettingsPanel
      id={AccountSettingsId.PhoneNumber}
      title={<Trans message="Change Mobile Number" />}
      actions={
        <Button
          variant="flat"
          color="primary"
          disabled={sendOtp.isPending}
          onClick={requestCode}
        >
          <Trans message="Send code" />
        </Button>
      }
    >
      <TextField
        readOnly
        label={<Trans message="Email" />}
        value={user.email}
      />
    </AccountSettingsPanel>
  );
}
